// verifierFactories.js — wire-dead-engines E1-WS1-T01.
// @arch:pure-engine
//
// The MISSING half of the AUTO_CORRECT gate. agentLoop's verify gate consumes a
// `verifiers` map ({console?,types?,lint?,smoke?,build?}) where each verifier is an
// async () => { passed, evidence }. Until now ONLY `previewConsoleVerifier` existed
// (oracle.js) — so a real Code turn could verify nothing but the live-preview console.
// These factories author the lint / types / smoke verifiers the gate always wanted.
//
// PURE BY CONTRACT (Tier 1 lib purity / the Dependency Rule): NO react, NO window, NO
// direct IPC. The shell port (`runCommand`) and the project `root` + `scripts` are
// INJECTED by the renderer composition root (SessionWorkspace). We reuse the existing,
// tested `runCheck` infra (verifyChecker.js) for exit-code → {passed,evidence} parsing.
//
// HONEST NO-OP (THE LAW): a verifier whose npm script is ABSENT resolves
// { passed:true, evidence:'noVerifier:<label>' } — SKIPPED, never a false red. `passed`
// is derived ONLY from the command exit code, never from a score/confidence field.

import { runCheck } from '../selfRepair/verifyChecker.js';
import { describeVerifier } from '../runtime/verifierDescriptor.js';

// npm scripts we map to a runnable, NON-INTERACTIVE command. `test` gets `--run` so a
// watch-mode runner exits instead of hanging the gate (mirrors verifyChecker).
const RUNNABLE = { test: 'npm test -- --run' };

/** The runnable command for an npm script, or null when the script is absent/blank. */
export function cmdForScript(scriptName, scripts) {
  if (!scripts || typeof scripts !== 'object') return null;
  const v = scripts[scriptName];
  if (typeof v !== 'string' || v.trim() === '') return null;
  return RUNNABLE[scriptName] || `npm run ${scriptName}`;
}

/**
 * Build a verifier for one npm script. Returns an async () => { passed, evidence }.
 * @param {object} deps
 * @param {string} deps.scriptName  the package.json script ('lint'|'typecheck'|'test'|…)
 * @param {string} deps.label       the verdict label ('lint'|'types'|'smoke')
 * @param {string} deps.root        project root (passed to the shell port)
 * @param {Function} deps.runCommand INJECTED shell port: ({command,cwd})=>Promise<{exitCode,stdout,stderr}>
 * @param {object=} deps.scripts    package.json `scripts` (or null)
 */
export function makeScriptVerifier({ scriptName, label, root, runCommand, scripts }) {
  const cmd = cmdForScript(scriptName, scripts);
  return describeVerifier(async () => {
    if (!cmd) return { passed: true, evidence: `noVerifier:${label}` }; // honest no-op (skipped)
    const r = await runCheck({ root, runCommand, cmd });
    return { passed: r.passed === true, evidence: r.passed ? `${label} ok` : r.evidence };
  }, { kind: 'script', root, commands: cmd ? [cmd] : [], label });
}

export function lintVerifier(deps) {
  return makeScriptVerifier({ ...deps, scriptName: 'lint', label: 'lint' });
}
export function typesVerifier(deps) {
  return makeScriptVerifier({ ...deps, scriptName: 'typecheck', label: 'types' });
}
export function smokeVerifier(deps) {
  return makeScriptVerifier({ ...deps, scriptName: 'test', label: 'smoke' });
}

/**
 * Build the default verifier MAP for the AUTO_CORRECT gate. Only includes a script
 * verifier when that script actually EXISTS — so a green verdict reflects real checks and
 * `noVerifiers` stays meaningful (a project with no scripts ⇒ console-only or empty map,
 * i.e. byte-identical to the pre-sprint fallback). `previewConsole` is the optional
 * console verifier the renderer builds from window.apolo.preview (injected, kept impure-free).
 *
 * @param {object} deps
 * @param {string} deps.root
 * @param {Function} deps.runCommand
 * @param {object=} deps.scripts
 * @param {Function=} deps.previewConsole  an already-built console verifier (or null)
 * @returns {object} verifiers map for runOracle / cfg.autoCorrect.verifiers
 */
export function buildDefaultVerifiers({ root, runCommand, scripts, previewConsole = null }) {
  const v = {};
  if (cmdForScript('lint', scripts)) v.lint = lintVerifier({ root, runCommand, scripts });
  if (cmdForScript('typecheck', scripts)) v.types = typesVerifier({ root, runCommand, scripts });
  if (cmdForScript('test', scripts)) v.smoke = smokeVerifier({ root, runCommand, scripts });
  if (typeof previewConsole === 'function') v.console = previewConsole;
  return v;
}
